import { useEffect, useState, createRef } from "react"
import { useSelector } from "react-redux"
import { RouteComponentProps, useHistory, useLocation, useParams, useRouteMatch } from "react-router"
import { Table } from "simplygrow-ui"
import { mdiContentPaste, mdiFilter, mdiFilterOutline, mdiPlus } from "@mdi/js"
import { difference } from "lodash"

import NavBar from "../../components/Navigation/NavBar"
import SectionHeader from "../../components/Headings/SectionHeaderPrimary"
import LoadingModal from "../../components/Modals/LoadingModal"
import AlertModalOneButton from "../../components/Modals/AlertModalOneButton"
import Modal from "../../components/Modals/Modal"
import FieldFilter from "../../components/GeneralUI/FieldFilter/FieldFilter"
import { ColorPalette } from "../../config/colors"

import { PeopleFlowCombinedReducer } from "../../store"
import { SessionService, TableService, FilterService } from "../../services"
import PickerContained from "../../components/BaseComponents/Pickers/PickerContained"
import { PickerHandlerEvent } from "../../components/BaseComponents/Pickers/Picker"
import { Toolbar } from "../../components/GeneralUI/Toolbar"
import { GroupService } from "../../services"
import { IdPasteModal } from "./IdPasteModal"
import { AssociationSettingsRepository, ProfileRepository } from "../../repositories"
import { Profile, ProfilePk } from "../../models"
import { Group, GroupMembers } from "../../types"
import { CustomFilterState } from "../../components/Tables/DataTable/DataTable"

import rowStyles from "./groupMembers.module.css"
import { TABLE_ROW_HEIGHT } from "../../constants/table"

type RouteParams = { groupId: string }

type GroupMemberProfile = {
  id: string
  name: string
  surname: string
  idPassport: string
  startDate: string
  terminationDate: string
}

type GroupMembersAddProps = {} & RouteComponentProps

const columnConfig = [
  { key: "idPassport", label: "ID / Passport", sizeFactor: 1 },
  { key: "name", label: "Name", sizeFactor: 1 },
  { key: "surname", label: "Surname", sizeFactor: 1 },
]

const tableService = new TableService()
const filterService = new FilterService()

const GroupMembersAdd = (props: GroupMembersAddProps) => {
  const primaryTableRef = createRef<HTMLDivElement>()
  const { groupId } = useParams() as RouteParams
  const history = useHistory()
  const match = useRouteMatch()
  const location = useLocation()

  const userIdPassport = useSelector((state: PeopleFlowCombinedReducer) => state.sessionManager.idPassport)
  const password = useSelector((state: PeopleFlowCombinedReducer) => state.sessionManager.password)
  const selectedAssociation = useSelector(
    (state: PeopleFlowCombinedReducer) => state.sessionManager.selectedAssociation,
  )
  const selectedCohort = useSelector((state: PeopleFlowCombinedReducer) => state.sessionManager.selectedCohort)
  const profileRepo = useSelector(
    (state: PeopleFlowCombinedReducer) => state.sessionManager.profileRepo,
  ) as ProfileRepository
  const associationRepo = useSelector(
    (state: PeopleFlowCombinedReducer) => state.sessionManager.associationRepo,
  ) as AssociationSettingsRepository
  const navMenuAccess = useSelector((state: PeopleFlowCombinedReducer) => state.sessionManager.navMenuAccess)

  const [profileData, setProfileData] = useState<GroupMemberProfile[]>([])
  const [searchString, setSearchString] = useState("")
  const [filterConfig, setFilterConfig] = useState<{ key: string; label: string }[]>([])
  const [loadingModalOpen, setLoadingModalOpen] = useState(false)
  const [idListModalOpen, setIdListModalOpen] = useState(false)
  const [jobTypeModalOpen, setJobTypeModalOpen] = useState(false)
  const [filterModalOpen, setFilterModalOpen] = useState(false)
  const [jobType, setJobType] = useState("")
  const [idPassportsPasted, setIdPassportsPasted] = useState<string[]>([])
  const [group, setGroup] = useState<Group>()
  const [tableHeight, setTableHeight] = useState(500)
  const [selectedProfilePks, setSelectedProfilePks] = useState<string[]>([])
  const [filters, setFilters] = useState<CustomFilterState[]>([])

  useEffect(() => {
    loadFreshGroupData()
  }, [groupId, selectedAssociation])

  useEffect(() => {
    if (primaryTableRef.current) {
      const tableTopY = primaryTableRef.current.getBoundingClientRect().top
      const browserWindowHeight = window.innerHeight
      setTableHeight(browserWindowHeight - tableTopY)
    }
  }, [primaryTableRef])

  const loadFreshGroupData = async () => {
    setLoadingModalOpen(true)
    const token = await SessionService.prepareAuthTokens(userIdPassport, password)
    const group = await GroupService.fetchGroup(selectedAssociation, selectedCohort, groupId, token)
    setGroup(group)
    updateTableData(filters, group)
    setLoadingModalOpen(false)
  }

  const saveNewMembersToGroup = async () => {
    if (group) {
      const newMembers = selectedProfilePks.reduce((acc: GroupMembers, pk: string) => {
        const profile = profileData.find((profile) => profile.id === pk)
        if (profile) {
          const memberDetails = {
            jobType,
            proposedStartDate: group.startDate,
            proposedTerminationDate: group.endDate,
            validTimeBasedDocs: [],
          }
          acc[pk] = memberDetails
        }
        return acc
      }, {})
      const members = { ...group.members, ...newMembers }
      const updatedGroup = { ...group, members }
      const token = await SessionService.prepareAuthTokens(userIdPassport, password)
      await GroupService.postGroup(selectedAssociation, updatedGroup, token)
      history.push(`/people/groups/${groupId}`)
    }
  }

  const updateTableData = (filtersToApply: CustomFilterState[], group: Group | undefined) => {
    const allProfiles = profileRepo.getAllProfileEntities()
    Object.keys(group?.members || {}).forEach((pk: string) => delete allProfiles[pk])
    let filteredData: GroupMemberProfile[] = prepareProfileDataForDisplay(allProfiles, filterConfig || [])
    if (filtersToApply.length) {
      filteredData = filterService.applyCustomFilters(filteredData, filtersToApply)
    }
    setProfileData(filteredData)
  }

  const prepareProfileDataForDisplay = (
    profiles: Record<ProfilePk, Profile> | undefined,
    filterConfig: { key: string; label: string }[],
  ): GroupMemberProfile[] => {
    if (profiles === undefined) {
      return []
    }

    let allProfilesArray: Profile[] = Object.values(profiles)
    const allProfilesToRender = allProfilesArray.filter((profile: Profile) => {
      let generalData: any = profile.getGeneralData()
      if (generalData === undefined) {
        generalData = { employmentStatus: "NONE" }
      }
      if (!profile.isPartOfCohort(selectedCohort)) {
        return false
      }

      const { employmentStatus, name, surname, idPassport } = generalData
      if (name && surname && idPassport && ["CANDIDATE", "EMPLOYEE"].indexOf(employmentStatus) > -1) {
        return true
      } else {
        return false
      }
    })
    return allProfilesToRender.map((profile) => {
      const pk = profile.getPk()
      const generalData = profile.getGeneralData()
      let filterFields = getFilterFields(generalData, filterConfig)
      let { name, surname, idPassport } = profile.getPersonalInfo()
      let { startDate, terminationDate } = generalData

      // Resorting to this ugly brute force approach after struggling to get types working in a forEach loop
      if (Array.isArray(name) && name.length) {
        name = name[0]
      }
      if (Array.isArray(surname) && surname.length) {
        surname = surname[0]
      }
      if (Array.isArray(idPassport) && idPassport.length) {
        idPassport = idPassport[0]
      }
      if (Array.isArray(startDate) && startDate.length) {
        startDate = startDate[0]
      }
      if (Array.isArray(terminationDate) && terminationDate.length) {
        terminationDate = terminationDate[0]
      }
      // // //
      let returnData = {
        id: pk,
        name: name,
        surname: surname,
        idPassport,
        startDate,
        terminationDate,
        ...filterFields,
      }
      return returnData
    })
  }

  const showSelectedProfiles = async (idPassportsPasted: string[]) => {
    const allProfiles = profileRepo.getAllProfileEntities()
    if (allProfiles) {
      const profilePks = Object.keys(allProfiles)
      if (idPassportsPasted && idPassportsPasted.length) {
        profilePks.forEach((pk: ProfilePk) => {
          const value = allProfiles[pk]
          const profileIdPassport = value.getIdPassport()
          if (!idPassportsPasted.includes(profileIdPassport)) {
            delete allProfiles[pk]
          }
        })
      }
      const profileData: GroupMemberProfile[] = prepareProfileDataForDisplay(allProfiles, filterConfig)
      const idPassportsUnknown = difference(idPassportsPasted, Object.keys(allProfiles))
      if (idPassportsUnknown.length > 0) {
        setProfileData(
          profileData.concat(
            idPassportsUnknown.map((idPassport: string) => ({
              idPassport,
              name: "?",
              surname: "?",
            })) as GroupMemberProfile[],
          ),
        )
      } else {
        setProfileData(profileData)
      }
      setSelectedProfilePks(idPassportsPasted)
    }

    return new Promise<void>((res) => res())
  }

  const getFilterFields = (
    generalData: Record<string, any>,
    filterConfig: { key: string; label: string }[],
  ): Record<string, any> => {
    let filterFields = {}
    filterConfig.forEach((configItem) => {
      if (configItem.key in generalData) {
        filterFields = { ...filterFields, [configItem.key]: generalData[configItem.key] }
      }
    })
    return filterFields
  }

  const searchHandler = (event: React.ChangeEvent<{ value: string }>) => {
    setSearchString(event.target.value)
  }

  const toggleJobTypeModal = () => {
    setJobTypeModalOpen((jobTypeModalOpen) => !jobTypeModalOpen)
  }
  const toggleIdListModal = () => {
    setIdListModalOpen((idListModalOpen) => !idListModalOpen)
  }

  const getCustomRowStyle = (rowId: string) => {
    if (profileData.find((profile) => profile.id === rowId && profile.name === "?")) {
      return `${rowStyles.rowHighlight} ${rowStyles.warningRed}`
    }
    return ""
  }

  const anyselectedProfilePks = selectedProfilePks.length > 0
  const organisationConfig = associationRepo.getOrganisationConfig(selectedAssociation)
  const availableJobTypes = group
    ? Object.keys(organisationConfig.divisionConfig.config[group.division || "ADMIN"].jobTypes)
    : []

  const toolbarActionButtons = [
    {
      iconPath: filters.length > 0 ? mdiFilter : mdiFilterOutline,
      iconColor: ColorPalette.PRIMARY_BLUE,
      onClick: () => setFilterModalOpen(true),
      label: "FILTER",
      title: "Set custom filters on table data",
      count: filters.length,
    },
    {
      iconPath: mdiContentPaste,
      onClick: toggleIdListModal,
      label: "IMPORT LIST",
      title: "Paste copied list of ID / Passport numbers",
    },
    {
      iconPath: mdiPlus,
      onClick: toggleJobTypeModal,
      label: "ADD TO GROUP",
      title: "Add individuals to group",
      disabled: !anyselectedProfilePks,
      iconColor: anyselectedProfilePks ? ColorPalette.PRIMARY_BLUE : ColorPalette.MEDIUM_GREY,
    },
  ]

  const { hasGroupAccess } = navMenuAccess
  const tableRowData = tableService.applySearch(searchString, profileData)
  const tableColumnDef = tableService.convertPfColumnConfigToGridColDef(columnConfig)
  return (
    <div style={{ ...styles.container }}>
      <NavBar
        match={match}
        location={location}
        history={history}
        reloadPageData={() => {
          setSearchString("")
          updateTableData([], group)
        }}
      />
      <SectionHeader
        style={styles.sectionHeader}
        searchString={searchString}
        textHandler={(e) => searchHandler(e)}
        onClick={() => null}
        key={`sectionHeader_${selectedAssociation}`}>
        {`${group?.name} | Add Group Member(s)`}
      </SectionHeader>
      <div style={styles.contentContainer}>
        <div style={styles.rightSide}>
          <Toolbar actionButtons={toolbarActionButtons} navButtons={{ left: { label: "BACK" } }} />
          <div style={styles.rightSideContent}>
            {/* <DataTable
              ref={primaryTableRef}
              tableData={profileData}
              columnConfig={columnConfig}
              tableWidth={10}
              filterState={filterState}
              onRowSelect={(selectedRows) => setselectedProfilePks(Object.keys(selectedRows))}
              selectedRowItemId={''}
              customFilterConfig={filterConfig}
              selectionEnabled={true}
              rowClickAsSelect={true}
              initialSelectedRows={idPassportsPasted}
              applyCustomRowStyle={getCustomRowStyle}
              key={`dataTable_${selectedAssociation}_${idPassportsPasted.length}`}
            /> */}
            <div ref={primaryTableRef} style={{ width: "100%" }}>
              <Table
                rows={tableRowData}
                columns={tableColumnDef}
                tableHeight={tableHeight}
                rowHeight={TABLE_ROW_HEIGHT}
                rowSelection={true}
                onRowSelection={setSelectedProfilePks}
                getRowClassName={(params: any) => getCustomRowStyle(params.id)}
                key={`dataTable_${selectedAssociation}_${idPassportsPasted.length}`}
              />
            </div>
          </div>
        </div>
      </div>
      <AlertModalOneButton
        open={!hasGroupAccess}
        header="Not Authorised"
        body="You don't have permission to view employee/candidate info."
        buttonLabel="Ok"
        opaqueBackground={true}
        onClick={() => history.goBack()}
      />
      <LoadingModal open={loadingModalOpen}>Refreshing data...</LoadingModal>
      <AlertModalOneButton
        open={jobTypeModalOpen}
        header="Job Type"
        body={
          <PickerContained
            value={jobType}
            placeholder="Select a job type"
            searchEnabled={true}
            items={availableJobTypes}
            onChange={(e: PickerHandlerEvent) => setJobType(e.target.value)}
            style={{ width: "max-content", padding: "0.75em 3em 1.5em" }}
          />
        }
        buttonLabel="Ok"
        opaqueBackground={true}
        onClick={() => {
          saveNewMembersToGroup()
          toggleJobTypeModal()
        }}
        dismiss={toggleJobTypeModal}
      />
      <IdPasteModal open={idListModalOpen} close={toggleIdListModal} setIdPassportsPasted={showSelectedProfiles} />

      <Modal open={filterModalOpen} title="" contentContainerStyle={{ padding: 0 }}>
        <FieldFilter
          filtersOnLoad={filters}
          filterConfig={filterConfig}
          onDoneClick={(updatedFilters) => {
            setFilters(updatedFilters)
            updateTableData(updatedFilters, group)
            setFilterModalOpen(false)
          }}
        />
      </Modal>
    </div>
  )
}

const styles = {
  container: {
    display: "flex",
    flexDirection: "column" as "column",
    flex: 1,
    backgroundImage: `linear-gradient(to bottom, ${ColorPalette.SCREEN_TOP_GRADIENT}, ${ColorPalette.SCREEN_BOTTOM_GRADIENT})`,
    height: "100vh",
  },
  sectionHeader: {
    margin: "3.5% auto 1%",
  },
  contentContainer: {
    display: "flex",
    flex: 1,
    overflow: "auto",
  },
  rightSide: {
    display: "flex",
    flexDirection: "column" as "column",
    paddingInline: "min(3em, 3%)",
    width: "100%",
    overflow: "hidden",
  },
  rightSideContent: {
    boxShadow: "0px -1px 8px rgba(60,60,60, 0.1)",
    display: "flex",
    flex: 1,
    backgroundColor: ColorPalette.CARD_WHITE,
  },
  tableNavButtonContainer: {
    display: "flex",
    alignItems: "center",
  },
  toolbarButton: {
    width: 180,
    fontWeight: "bolder" as "bolder",
    fontSize: "0.8rem",
    color: ColorPalette.SECONDARY_TEXT,
    height: 40,
    ":hover": {
      color: ColorPalette.PRIMARY_BLUE,
    },
    ":active": {
      color: ColorPalette.DARK_GREY,
    },
  },
  pasteListModal: {
    textAlign: "center" as "center",
    alignSelf: "center",
    fontFamily: "roboto",
    color: ColorPalette.PRIMARY_TEXT,
    padding: "0 1.5em .75em",
    fontSize: "1.1em",
  },
}

export default GroupMembersAdd
